/**
 * /settings/ai (v11, admin-style) — Inteligência artificial no idioma de
 * configuração do próprio app (padrão da conexão Asaas): chave de API
 * Anthropic por tenant (password, placeholder mascarado), Modelo (select) e
 * Ativado, com status da integração. O TiFlux vende créditos — aqui o modelo
 * é chave própria do cliente.
 */
import { useState, type FormEvent } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ShieldAlert, Sparkles } from 'lucide-react';
import {
  AI_ERROR_LABELS,
  AI_MODEL_LABELS,
  AiModelSchema,
  type AiIntegration,
  type AiModel,
} from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { api } from '@/lib/api-client';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, PageSpinner } from '@/components/ui/misc';
import { Section } from '@/features/settings/section';
import { SettingsBreadcrumb } from '@/features/settings/settings-ui';

const AI_INTEGRATION_KEY = ['erp', 'ai', 'integration'] as const;

export function aiIntegrationQuery() {
  return queryOptions({
    queryKey: AI_INTEGRATION_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<AiIntegration>('/ai/integration', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

const ERROR_LABELS: Record<string, string> = {
  ...AI_ERROR_LABELS,
  api_key_required: 'Informe a chave de API para ativar a integração.',
};

/** Rótulos da tela em camada sobre o fallback genérico (padrão chats-format). */
export function aiErrorMessage(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'error' in e) {
    const code = (e as { error: unknown }).error;
    if (typeof code === 'string') {
      const label = ERROR_LABELS[code];
      if (label) return label;
    }
  }
  return genericErrorMessage(e);
}

export function AiSettingsPage() {
  const user = useAuthUser();
  if (user?.role !== 'admin') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          A integração de inteligência artificial é configurada por administradores da conta.
        </p>
      </div>
    );
  }
  return <AiSettingsAdmin />;
}

function AiSettingsAdmin() {
  const integrationQ = useQuery(aiIntegrationQuery());

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card px-4 pt-3 pb-4 shadow-xs sm:px-5">
        <SettingsBreadcrumb section="Geral" page="Inteligência artificial" />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <h1 className="text-xl leading-7 font-semibold">Inteligência artificial</h1>
          {integrationQ.data ? (
            <Badge variant={integrationQ.data.active ? 'success' : 'default'}>
              {integrationQ.data.active ? 'Ativada' : 'Desativada'}
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Sugestão de resposta e resumo de tickets com a sua própria chave da API Anthropic.
        </p>
      </div>

      {integrationQ.isPending ? (
        <PageSpinner />
      ) : integrationQ.isError ? (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-destructive shadow-xs">
          Erro ao carregar a integração: {aiErrorMessage(integrationQ.error)}
        </p>
      ) : (
        <AiIntegrationForm integration={integrationQ.data} />
      )}
    </div>
  );
}

function AiIntegrationForm({ integration }: { integration: AiIntegration }) {
  const qc = useQueryClient();
  const configured = integration.apiKeyMasked != null;
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(integration.model);
  const [active, setActive] = useState(integration.active);
  const [saved, setSaved] = useState(false);

  // Refetch fora do submit não sobrescreve digitação — re-sincroniza (durante
  // o render) só quando os valores do servidor mudam (padrão Asaas).
  const [prevServer, setPrevServer] = useState({
    model: integration.model,
    active: integration.active,
  });
  if (prevServer.model !== integration.model || prevServer.active !== integration.active) {
    setPrevServer({ model: integration.model, active: integration.active });
    setModel(integration.model);
    setActive(integration.active);
  }

  const save = useMutation({
    mutationFn: async () => {
      const key = apiKey.trim();
      const res = await api<AiIntegration>('/ai/integration', {
        method: 'PUT',
        body: { ...(key ? { apiKey: key } : {}), model, active },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(AI_INTEGRATION_KEY, data);
      // O status alimenta os botões de IA no detalhe do ticket.
      void qc.invalidateQueries({ queryKey: ['erp', 'ai', 'status'] });
      setApiKey('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!configured && !apiKey.trim()) return;
    save.mutate();
  };

  return (
    <Section
      title="Integração Anthropic"
      description="Chave de API da sua conta Anthropic (sk-ant-...), modelo utilizado e ativação dos recursos de IA."
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ai-api-key">
              Chave de API{' '}
              {configured ? null : <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="ai-api-key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                configured && integration.apiKeyMasked
                  ? `Configurada (${integration.apiKeyMasked}) — cole para trocar`
                  : 'sk-ant-...'
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ai-model">Modelo</Label>
            <Select
              id="ai-model"
              value={model}
              onChange={(e) => setModel(AiModelSchema.parse(e.target.value))}
            >
              {AiModelSchema.options.map((value: AiModel) => (
                <option key={value} value={value}>
                  {AI_MODEL_LABELS[value]}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="ai-active"
            aria-label="Integração de IA ativada"
            checked={active}
            onCheckedChange={setActive}
          />
          <Label htmlFor="ai-active" className="cursor-pointer">
            Ativada
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="sm"
            disabled={save.isPending || (!configured && apiKey.trim() === '')}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
          {saved ? (
            <span className="flex items-center gap-1 text-xs text-[#52C41A]">
              <CheckCircle2 className="size-3.5" />
              Integração salva.
            </span>
          ) : null}
          {save.error ? (
            <span className="text-xs text-destructive" role="alert">
              {aiErrorMessage(save.error)}
            </span>
          ) : null}
        </div>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="size-3.5 shrink-0" />
          {integration.active && configured
            ? 'Integração ativa — os botões "Sugerir resposta" e "Resumir com IA" aparecem no detalhe dos tickets.'
            : 'Com a integração ativa, os tickets ganham os botões "Sugerir resposta" e "Resumir com IA".'}
        </p>
      </form>
    </Section>
  );
}
